'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS,
    WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS,
    WHATSAPP_TEMPLATE_CATEGORY_LABELS,
    WHATSAPP_TEMPLATE_CONTENT_TYPE_LABELS,
    WhatsappTemplate,
    WhatsappTemplateApprovalStatus,
    WhatsappTemplateCategory,
    deleteWhatsappTemplate,
    getWhatsappTemplates,
    adoptTemplatesFromMeta,
    previewWhatsappTemplate,
    submitWhatsappTemplateApproval,
    syncAllWhatsappTemplates,
    syncWhatsappTemplate,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheckCircle,
    MdCloudDownload,
    MdDelete,
    MdEdit,
    MdRefresh,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const CATEGORIES: WhatsappTemplateCategory[] = [
    'MARKETING',
    'UTILITY',
    'AUTHENTICATION',
]
const APPROVAL_STATUSES: WhatsappTemplateApprovalStatus[] = [
    'DRAFT',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'PAUSED',
    'DISABLED',
]

function formatDate(date: string | null): string {
    if (!date) return '-'
    try {
        return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    } catch {
        return '-'
    }
}

function extractVariables(body: string): number[] {
    const matches = Array.from(body.matchAll(/\{\{\s*(\d+)\s*\}\}/g))
    const set = new Set<number>()
    for (const m of matches) set.add(parseInt(m[1], 10))
    return Array.from(set).sort((a, b) => a - b)
}


const WhatsappTemplatesPage = () => {
    const { canRead, canCreate, canUpdate, canDelete } = useResourcePermissions(
        'messaging',
        'whatsapp_templates'
    )
    const { user } = useAuth()
    const navigate = useNavigate()
    // A adoção enxerga apenas a conta da PRÓPRIA organização (resolvida pelo
    // token, no backend), então quem pode criar template pode reconhecer os que
    // já existem lá. A restrição a `companyId === 1` vinha de quando havia uma
    // conta só, a da plataforma.
    const canAdoptTemplates = canCreate

    const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState<{
        category?: WhatsappTemplateCategory
        approvalStatus?: WhatsappTemplateApprovalStatus
        active?: boolean
    }>({})

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)

    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [previewTemplate, setPreviewTemplate] =
        useState<WhatsappTemplate | null>(null)
    const [previewValues, setPreviewValues] = useState<Record<string, string>>(
        {}
    )
    const [previewRendered, setPreviewRendered] = useState<string>('')

    const fetchTemplates = async () => {
        setLoading(true)
        try {
            const res = await getWhatsappTemplates(filters)
            if (res.success) {
                setTemplates(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar templates')
            }
        } catch (e) {
            toast.error('Falha na requisição')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (canRead) fetchTemplates()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canRead, JSON.stringify(filters)])

    const handleDelete = async () => {
        if (deletingId == null) return
        const res = await deleteWhatsappTemplate(deletingId)
        if (res.success) {
            toast.success('Template removido')
            setShowDeleteModal(false)
            setDeletingId(null)
            fetchTemplates()
        } else {
            toast.error(res.error || 'Erro ao remover')
        }
    }

    const handleSubmitApproval = async (tpl: WhatsappTemplate) => {
        const res = await submitWhatsappTemplateApproval(tpl.id)
        if (res.success) {
            toast.success('Aprovação submetida')
            fetchTemplates()
        } else {
            toast.error(res.error || 'Falha ao submeter')
        }
    }

    const handleSync = async (tpl: WhatsappTemplate) => {
        const res = await syncWhatsappTemplate(tpl.id)
        if (res.success) {
            toast.success('Status atualizado')
            fetchTemplates()
        } else {
            toast.error(res.error || 'Falha ao sincronizar')
        }
    }

    const handleSyncAll = async () => {
        const res = await syncAllWhatsappTemplates()
        if (res.success) {
            toast.success(`${res.result.synced} template(s) sincronizado(s)`)
            fetchTemplates()
        } else {
            toast.error(res.error || 'Falha na sincronização')
        }
    }

    const [adopting, setAdopting] = useState(false)
    /**
     * Reconhece os templates que já existem na conta da Meta, preenchendo o id
     * e o nome remoto de cada um. Não cria nem reenvia nada para aprovação —
     * eles sempre estiveram lá.
     */
    const handleAdoptFromMeta = async () => {
        setAdopting(true)
        try {
            const res = await adoptTemplatesFromMeta()
            if (res.success) {
                const { adopted, alreadyAdopted, unmatchedLocal } = res.result
                const semPar = unmatchedLocal.length
                    ? `, ${unmatchedLocal.length} sem par na Meta`
                    : ''
                toast.success(
                    `Adoção concluída: ${adopted} adotado(s), ${alreadyAdopted} já adotado(s)${semPar}`
                )
                fetchTemplates()
            } else {
                toast.error(
                    res.error || 'Falha ao adotar os templates da Meta'
                )
            }
        } finally {
            setAdopting(false)
        }
    }

    const openPreview = async (tpl: WhatsappTemplate) => {
        setPreviewTemplate(tpl)
        const vars = extractVariables(tpl.bodyText)
        const initial: Record<string, string> = {}
        for (const idx of vars) initial[String(idx)] = ''
        setPreviewValues(initial)
        setPreviewRendered(tpl.bodyText)
        setShowPreviewModal(true)
    }

    const refreshPreview = async () => {
        if (!previewTemplate) return
        const res = await previewWhatsappTemplate(
            previewTemplate.id,
            previewValues
        )
        if (res.success) {
            setPreviewRendered(res.result.rendered)
        }
    }

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">
                    Você não tem permissão para acessar templates WhatsApp.
                </div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-2xl font-bold">Templates WhatsApp</h1>
                    <p className="text-sm text-base-content/70">
                        Gerencie templates aprovados pela Meta para envio via
                        WhatsApp Business.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canAdoptTemplates && (
                        <button
                            type="button"
                            className="btn btn-outline btn-sm gap-2"
                            onClick={handleAdoptFromMeta}
                            disabled={adopting}
                        >
                            {adopting ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdCloudDownload size={18} />
                            )}
                            Adotar da Meta
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-2"
                        onClick={handleSyncAll}
                    >
                        <MdRefresh size={18} /> Sincronizar tudo
                    </button>
                    {canCreate && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-2"
                            onClick={() =>
                                navigate('/messaging/whatsapp-templates/new')
                            }
                        >
                            <MdAdd size={18} /> Novo template
                        </button>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="flex flex-col gap-1 w-full">
                            <span className="text-sm font-medium text-base-content/80">
                                Categoria
                            </span>
                            <select
                                className="select select-bordered select-sm"
                                value={filters.category ?? ''}
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        category: (e.target.value ||
                                            undefined) as
                                            | WhatsappTemplateCategory
                                            | undefined,
                                    }))
                                }
                            >
                                <option value="">Todas</option>
                                {CATEGORIES.map(c => (
                                    <option key={c} value={c}>
                                        {WHATSAPP_TEMPLATE_CATEGORY_LABELS[c]}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 w-full">
                            <span className="text-sm font-medium text-base-content/80">
                                Status de Aprovação
                            </span>
                            <select
                                className="select select-bordered select-sm"
                                value={filters.approvalStatus ?? ''}
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        approvalStatus: (e.target.value ||
                                            undefined) as
                                            | WhatsappTemplateApprovalStatus
                                            | undefined,
                                    }))
                                }
                            >
                                <option value="">Todos</option>
                                {APPROVAL_STATUSES.map(s => (
                                    <option key={s} value={s}>
                                        {
                                            WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[
                                                s
                                            ]
                                        }
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 w-full">
                            <span className="text-sm font-medium text-base-content/80">
                                Ativos
                            </span>
                            <select
                                className="select select-bordered select-sm"
                                value={
                                    filters.active == null
                                        ? ''
                                        : filters.active
                                          ? 'true'
                                          : 'false'
                                }
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        active:
                                            e.target.value === ''
                                                ? undefined
                                                : e.target.value === 'true',
                                    }))
                                }
                            >
                                <option value="">Todos</option>
                                <option value="true">Apenas ativos</option>
                                <option value="false">Apenas inativos</option>
                            </select>
                        </label>
                    </div>

                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="loading loading-spinner loading-md" />
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="text-center text-base-content/60 p-8">
                            Nenhum template encontrado.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Categoria</th>
                                        <th>Tipo</th>
                                        <th>Status</th>
                                        <th>Atualizado</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map(tpl => (
                                        <tr key={tpl.id}>
                                            <td>
                                                <div className="font-semibold">
                                                    {tpl.friendlyName}
                                                </div>
                                                <div className="text-xs text-base-content/60">
                                                    {tpl.name}
                                                </div>
                                            </td>
                                            <td>
                                                {
                                                    WHATSAPP_TEMPLATE_CATEGORY_LABELS[
                                                        tpl.category
                                                    ]
                                                }
                                            </td>
                                            <td>
                                                {
                                                    WHATSAPP_TEMPLATE_CONTENT_TYPE_LABELS[
                                                        tpl.contentType
                                                    ]
                                                }
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS[tpl.approvalStatus]}`}
                                                >
                                                    {
                                                        WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[
                                                            tpl.approvalStatus
                                                        ]
                                                    }
                                                </span>
                                                {tpl.approvalRejectionReason && (
                                                    <div
                                                        className="text-xs text-error mt-1 max-w-xs truncate"
                                                        title={
                                                            tpl.approvalRejectionReason
                                                        }
                                                    >
                                                        {
                                                            tpl.approvalRejectionReason
                                                        }
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-xs">
                                                {formatDate(tpl.updatedAt)}
                                            </td>
                                            <td className="text-right">
                                                <div className="join">
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs join-item"
                                                        title="Pré-visualizar"
                                                        onClick={() =>
                                                            openPreview(tpl)
                                                        }
                                                    >
                                                        <MdVisibility />
                                                    </button>
                                                    {(tpl.approvalStatus ===
                                                        'DRAFT' ||
                                                        tpl.approvalStatus ===
                                                            'REJECTED') &&
                                                        canUpdate && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-ghost btn-xs join-item text-success"
                                                                title="Submeter aprovação"
                                                                onClick={() =>
                                                                    handleSubmitApproval(
                                                                        tpl
                                                                    )
                                                                }
                                                            >
                                                                <MdCheckCircle />
                                                            </button>
                                                        )}
                                                    {canUpdate && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs join-item"
                                                            title="Sincronizar status"
                                                            onClick={() =>
                                                                handleSync(tpl)
                                                            }
                                                        >
                                                            <MdRefresh />
                                                        </button>
                                                    )}
                                                    {canUpdate && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs join-item"
                                                            title="Editar"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/messaging/whatsapp-templates/edit/${tpl.id}`
                                                                )
                                                            }
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs join-item text-error"
                                                            title="Remover"
                                                            onClick={() => {
                                                                setDeletingId(
                                                                    tpl.id
                                                                )
                                                                setShowDeleteModal(
                                                                    true
                                                                )
                                                            }}
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>


            {showPreviewModal && previewTemplate && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg mb-4">
                            Pré-visualizar: {previewTemplate.friendlyName}
                        </h3>
                        {(previewTemplate.metaTemplateId ||
                            previewTemplate.contentSid) && (
                            <div className="mb-4 flex flex-col gap-1">
                                <span className="text-xs font-medium text-base-content/70">
                                    ID externo
                                </span>
                                <code className="text-xs bg-base-200 rounded px-2 py-1.5 break-all">
                                    {previewTemplate.metaTemplateId ??
                                        previewTemplate.contentSid}
                                </code>
                            </div>
                        )}
                        {extractVariables(previewTemplate.bodyText).length >
                            0 && (
                            <div className="mb-4">
                                <div className="text-sm font-semibold mb-2">
                                    Valores das variáveis
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                    {extractVariables(
                                        previewTemplate.bodyText
                                    ).map(idx => (
                                        <div
                                            key={idx}
                                            className="flex flex-col gap-1 w-full"
                                        >
                                            <label
                                                htmlFor={`preview-var-${idx}`}
                                                className="text-xs font-medium text-base-content/70"
                                            >
                                                {'{{'}
                                                {idx}
                                                {'}}'}
                                            </label>
                                            <input
                                                id={`preview-var-${idx}`}
                                                type="text"
                                                className="input input-bordered input-sm w-full"
                                                value={
                                                    previewValues[
                                                        String(idx)
                                                    ] ?? ''
                                                }
                                                onChange={e =>
                                                    setPreviewValues(v => ({
                                                        ...v,
                                                        [String(idx)]:
                                                            e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm gap-2 mt-3"
                                    onClick={refreshPreview}
                                >
                                    <MdRefresh /> Atualizar pré-visualização
                                </button>
                            </div>
                        )}
                        <div className="bg-base-200 p-4 rounded-lg text-sm whitespace-pre-wrap">
                            {previewTemplate.headerText && (
                                <div className="font-bold mb-2">
                                    {previewTemplate.headerText}
                                </div>
                            )}
                            <div>{previewRendered}</div>
                            {previewTemplate.footerText && (
                                <div className="text-xs text-base-content/60 mt-2">
                                    {previewTemplate.footerText}
                                </div>
                            )}
                        </div>
                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowPreviewModal(false)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDeletingId(null)
                }}
                onConfirm={handleDelete}
                title="Remover template"
                message="Tem certeza que deseja remover este template? Se ele ainda não foi aprovado, será removido também do provedor WhatsApp."
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}

export default WhatsappTemplatesPage
